/**
 * Teen Share the Moment Page
 * Premium shareable cards for teen achievements - designed for viral sharing.
 * Matches the main app's share functionality with teen-specific vibrant design.
 * Uses TeenThemeContext for customizable look and feel.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Share2,
  Download,
  Trophy,
  Flame,
  Rocket,
  Star,
  Zap,
  X,
  Check
} from "lucide-react";
import axios from "axios";
import QRCode from "qrcode";
import { toast } from "sonner";
import { TeenThemeProvider, useTeenTheme } from "../../contexts/TeenThemeContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = window.location.origin;

// Teen-specific gradient themes
const TEEN_GRADIENTS = {
  challenge_complete: {
    colors: ["#9333ea", "#ec4899", "#f97316"],
    stops: [0, 0.5, 1]
  },
  week_complete: {
    colors: ["#3b82f6", "#8b5cf6", "#d946ef"],
    stops: [0, 0.5, 1]
  },
  journey_complete: {
    colors: ["#f59e0b", "#ef4444", "#ec4899"],
    stops: [0, 0.4, 1]
  },
  streak: {
    colors: ["#f97316", "#ef4444", "#dc2626"],
    stops: [0, 0.5, 1]
  },
  xp_milestone: {
    colors: ["#10b981", "#14b8a6", "#06b6d4"],
    stops: [0, 0.5, 1]
  }
};

const TeenSharePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [journeyHistory, setJourneyHistory] = useState([]);
  
  const canvasRef = useRef(null);
  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    loadStats();
    loadJourneyHistory();
  }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const loadJourneyHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/challenge/history`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setJourneyHistory(res.data.history || []);
    } catch (err) {
      console.error("Error loading history:", err);
    }
  };

  // Available templates based on progress
  const getAvailableTemplates = () => {
    if (!stats) return [];
    
    const templates = [];
    const daysCompleted = stats.days_completed || 0;
    
    // Current day progress
    if (daysCompleted > 0 && daysCompleted < 21) {
      templates.push({
        id: "day_complete",
        title: `Day ${daysCompleted}`,
        subtitle: "Challenge Complete",
        badge: `${daysCompleted}/21`,
        theme: "challenge_complete",
        icon: "rocket",
        message: `I completed Day ${daysCompleted} of my 21-Day Confidence Journey!`
      });
    }
    
    // Week milestones
    const weeks = Math.floor(daysCompleted / 7);
    if (weeks >= 1 && daysCompleted < 21) {
      templates.push({
        id: "week_complete",
        title: `Week ${weeks} Done!`,
        subtitle: "Building Confidence Daily",
        badge: `${weeks} Week${weeks > 1 ? 's' : ''}`,
        theme: "week_complete",
        icon: "star",
        message: `I just completed Week ${weeks} of my 21-Day Confidence Journey!`
      });
    }
    
    // Streak
    if (stats.current_streak >= 3) {
      templates.push({
        id: "streak",
        title: `${stats.current_streak} Day Streak`,
        subtitle: "Consistency is Power",
        badge: "On Fire!",
        theme: "streak",
        icon: "flame",
        message: `I'm on a ${stats.current_streak} day streak in my Confidence Journey!`
      });
    }
    
    // Full journey complete
    if (daysCompleted >= 21) {
      templates.push({
        id: "journey_complete",
        title: "21 Days Complete!",
        subtitle: "Confidence Champion",
        badge: "Champion",
        theme: "journey_complete",
        icon: "trophy",
        message: "I completed the 21-Day Confidence Journey! 🏆"
      });
    }
    
    // XP milestones
    const xp = stats.total_xp || 0;
    if (xp >= 1000) {
      const milestoneXP = xp >= 3000 ? 3000 : xp >= 2000 ? 2000 : 1000;
      templates.push({
        id: "xp_milestone",
        title: `${milestoneXP}+ XP`,
        subtitle: "Level Up Achievement",
        badge: "Power Up",
        theme: "xp_milestone",
        icon: "zap",
        message: `I've earned ${xp} XP on my Confidence Journey!`
      });
    }
    
    return templates;
  };

  // Generate premium share card
  const generateCard = useCallback(async (template) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1350;
    
    canvas.width = width;
    canvas.height = height;

    // Get gradient colors
    const gradientConfig = TEEN_GRADIENTS[template.theme] || TEEN_GRADIENTS.challenge_complete;
    
    // Draw gradient background
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradientConfig.colors.forEach((color, i) => {
      gradient.addColorStop(gradientConfig.stops[i], color);
    });
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add radial glow
    const glow = ctx.createRadialGradient(width/2, height/2 - 100, 0, width/2, height/2 - 100, 500);
    glow.addColorStop(0, 'rgba(255, 255, 255, 0.15)');
    glow.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, height);

    // Add subtle grain
    ctx.globalAlpha = 0.04;
    for (let i = 0; i < 8000; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#FFFFFF' : '#000000';
      ctx.fillRect(Math.random() * width, Math.random() * height, 1, 1);
    }
    ctx.globalAlpha = 1;

    // Floating particles
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const size = Math.random() * 80 + 20;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }

    // Header - TEEN ZONE
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.font = '600 24px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.letterSpacing = '0.3em';
    ctx.fillText('TEEN ZONE', width / 2, 100);

    // Badge
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    const badgeWidth = ctx.measureText(template.badge).width + 60;
    roundRect(ctx, (width - badgeWidth) / 2, 130, badgeWidth, 50, 25);
    ctx.fill();
    
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 22px "Inter", sans-serif';
    ctx.fillText(template.badge, width / 2, 163);

    // Icon circle
    const iconY = 350;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.beginPath();
    ctx.arc(width / 2, iconY, 100, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.beginPath();
    ctx.arc(width / 2, iconY, 75, 0, Math.PI * 2);
    ctx.fill();

    // Icon emoji
    ctx.font = '100px sans-serif';
    const iconEmojis = {
      trophy: '🏆',
      flame: '🔥',
      rocket: '🚀',
      star: '⭐',
      zap: '⚡'
    };
    ctx.fillText(iconEmojis[template.icon] || '🏆', width / 2, iconY + 40);

    // Main title
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 100px "Inter", sans-serif';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 30;
    ctx.fillText(template.title, width / 2, 580);
    ctx.shadowBlur = 0;

    // Subtitle
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = '500 44px "Inter", sans-serif';
    ctx.fillText(template.subtitle, width / 2, 660);

    // Divider
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(width / 2 - 150, 730);
    ctx.lineTo(width / 2 + 150, 730);
    ctx.stroke();

    // Journey title
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '600 36px "Inter", sans-serif';
    ctx.fillText('21-Day Confidence Journey', width / 2, 800);

    // Quote box
    const boxWidth = width - 120;
    const boxX = (width - boxWidth) / 2;
    const boxY = 870;
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    roundRect(ctx, boxX, boxY, boxWidth, 140, 24);
    ctx.fill();

    // Quote
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.font = 'italic 32px "Georgia", serif';
    ctx.fillText('"I\'m showing up for myself,', width / 2, boxY + 55);
    ctx.fillText('every single day."', width / 2, boxY + 100);

    // QR Code
    const qrY = 1080;
    try {
      const qrDataUrl = await QRCode.toDataURL(`${APP_URL}/teen`, {
        width: 120,
        margin: 0,
        color: {
          dark: '#FFFFFF',
          light: '#00000000'
        },
        errorCorrectionLevel: 'M'
      });
      
      const qrImage = new Image();
      await new Promise((resolve, reject) => {
        qrImage.onload = resolve;
        qrImage.onerror = reject;
        qrImage.src = qrDataUrl;
      });
      
      const qrSize = 100;
      ctx.drawImage(qrImage, (width - qrSize) / 2, qrY, qrSize, qrSize);
    } catch (err) {
      console.log("QR code error:", err);
    }

    // Footer CTA
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '500 24px "Inter", sans-serif';
    ctx.fillText('Start your journey at reclaim.app', width / 2, qrY + 150);

    // Reclaim branding
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '400 20px "Inter", sans-serif';
    ctx.fillText('RECLAIM • SCHOOL OF CONSCIOUSNESS', width / 2, qrY + 200);

    return canvas.toDataURL('image/png');
  }, []);

  // Helper for rounded rectangles
  const roundRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setIsGenerating(true);
    setPreviewUrl(null);
    
    const url = await generateCard(template);
    setPreviewUrl(url);
    setIsGenerating(false);
  };

  const handleDownload = async () => {
    if (!previewUrl) return;
    
    const link = document.createElement('a');
    link.download = `teen-zone-${selectedTemplate.id}.png`;
    link.href = previewUrl;
    link.click();
    
    toast.success("Image saved!", { duration: 3000 });
  };

  const handleShare = async () => {
    if (!previewUrl || !selectedTemplate) return;
    
    setIsGenerating(true);
    
    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const file = new File([blob], `teen-zone-${selectedTemplate.id}.png`, { type: 'image/png' });

      const canShareFiles = navigator.share && navigator.canShare && navigator.canShare({ files: [file] });
      
      if (canShareFiles) {
        await navigator.share({
          files: [file],
          title: selectedTemplate.title,
          text: selectedTemplate.message
        });
      } else {
        // Fallback to download
        handleDownload();
        toast.success("Image saved! Share it to your socials.", { duration: 4000 });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("Share error:", err);
        handleDownload();
      }
    }
    
    setIsGenerating(false);
  };

  const templates = getAvailableTemplates();

  // Preview modal
  if (selectedTemplate && previewUrl) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23]">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <button
            onClick={() => { setSelectedTemplate(null); setPreviewUrl(null); }}
            className="text-white/60 hover:text-white flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <span className="text-white/60 text-sm">Share Your Moment</span>
          <div className="w-16" />
        </div>

        {/* Preview */}
        <div className="px-6 pb-8">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative aspect-[4/5] max-w-sm mx-auto rounded-3xl overflow-hidden shadow-2xl"
          >
            <img 
              src={previewUrl} 
              alt="Share card preview" 
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Message */}
          <p className="text-white/70 text-center mt-6 mb-6 max-w-sm mx-auto">
            {selectedTemplate.message}
          </p>

          {/* Action buttons */}
          <div className="flex gap-4 max-w-sm mx-auto">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownload}
              disabled={isGenerating}
              className="flex-1 py-4 rounded-xl bg-white/10 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download size={20} />
              Save
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleShare}
              disabled={isGenerating}
              className="flex-1 py-4 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30 disabled:opacity-50"
            >
              <Share2 size={20} />
              Share
            </motion.button>
          </div>
        </div>

        {/* Hidden canvas */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23]">
      {/* Hidden canvas */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Header */}
      <div className="p-4">
        <button
          onClick={() => navigate("/teen")}
          className="text-white/60 hover:text-white flex items-center gap-2"
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      {/* Title */}
      <div className="px-6 pt-2 pb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Share the Moment</h1>
        <p className="text-white/60">Celebrate and inspire others</p>
      </div>

      {/* Templates */}
      {templates.length > 0 ? (
        <div className="px-6 pb-8 space-y-4">
          {templates.map((template, index) => {
            const gradientConfig = TEEN_GRADIENTS[template.theme];
            const gradientClass = template.theme === 'journey_complete' 
              ? 'from-amber-500 via-orange-500 to-pink-500'
              : template.theme === 'streak'
                ? 'from-orange-500 via-red-500 to-pink-500'
                : template.theme === 'xp_milestone'
                  ? 'from-emerald-500 via-teal-500 to-cyan-500'
                  : template.theme === 'week_complete'
                    ? 'from-blue-500 via-purple-500 to-pink-500'
                    : 'from-purple-500 via-pink-500 to-orange-500';
            
            const IconComponent = template.icon === 'trophy' ? Trophy :
              template.icon === 'flame' ? Flame :
              template.icon === 'rocket' ? Rocket :
              template.icon === 'zap' ? Zap : Star;
            
            return (
              <motion.button
                key={template.id}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleSelectTemplate(template)}
                disabled={isGenerating}
                className={`w-full p-5 rounded-2xl bg-gradient-to-r ${gradientClass} shadow-xl flex items-center gap-4 text-left disabled:opacity-50`}
                data-testid={`share-template-${template.id}`}
              >
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <IconComponent size={28} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-0.5">{template.title}</h3>
                  <p className="text-white/80 text-sm">{template.subtitle}</p>
                </div>
                <div className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-semibold">
                  {template.badge}
                </div>
              </motion.button>
            );
          })}

          {/* Tip */}
          <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10">
            <p className="text-white/60 text-sm text-center">
              💡 Share your progress on social media to inspire your friends to start their own confidence journey!
            </p>
          </div>
        </div>
      ) : (
        <div className="px-6 pb-8">
          <div className="p-8 rounded-2xl bg-white/5 border border-white/10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
              <Trophy size={32} className="text-purple-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">Start Your Journey</h3>
            <p className="text-white/50 text-sm mb-4">
              Complete challenges to unlock shareable moments!
            </p>
            <button
              onClick={() => navigate("/teen/challenge")}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold"
            >
              Begin Challenge
            </button>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {isGenerating && !previewUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Share2 size={40} className="text-purple-500" />
            </motion.div>
            <p className="text-white/60 mt-4">Creating your moment...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeenSharePage;
